import {useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import machineIcon from '../../../../assets/image/inputGroups/machinery.svg';
import Button from '../../../../components/button';
import CustomCheckbox from '../../../../components/mazraeKit/customCheckbox';
import CustomHeader from '../../../../components/mazraeKit/headers/homeHeader';
import NoItemFoundPage from '../../../../components/noItemFoundPage';
import SpinnerLoading from '../../../../components/SpinnerLoading';
import {useAuth} from '../../../../context/authContext';
import {StoreOrderSteps} from '../../../../services/hookService/getOrderDetails/helper';
import useGetOrderDetails from '../../../../services/hookService/getOrderDetails/useGetOrderDetails';
import usePostUserOrderDelivered from '../../../../services/hookService/postUserOrderDelivered/usePostUserOrderDelivered';
import {getPersianDateTime} from '../../../../utilities/helper/GetPersianDateTime';
import LendTechPaySheet from '../../../lendtech/components/Lendtechpaysheet/Lendtechpaysheet';
import {TransactionTypesChoices} from '../../../shopping-basket/cash-payment/utils/helpers';
import BuyerInformation from './components/BuyerInformation';
import OrderedItems from './components/OrderedItems';
import OrderInformation from './components/OrderInformation';
import OrderSupplyingStatus from './components/OrderSupplyingStatus';
import SellerInformation from './components/SellerInformation';

// created_at : تاریخ ثبت
// supplyDate :  تاریخ دریافت
// delivery_date : تاریخ تحویل سفارش

const OrderDetail = () => {
  const navigate = useNavigate();
  const {orderGuid, storeOrderGuid} = useParams();
  const {supplierProfile} = useAuth();

  const [orderReceived, setOrderReceived] = useState<boolean>(false);
  const [isPayFlowOpen, setIsPayFlowOpen] = useState(false);

  const [isErrorOpen, setIsOpenError] = useState(false);

  const {data, isLoading} = useGetOrderDetails({orderGuid, storeOrderGuid});
  // Flatten all order items across all store_order
  const allOrderItems = useMemo(() => {
    if (!data?.store_order?.length) return [];
    return data?.store_order.flatMap(storeOrder =>
      storeOrder.order_items.map(item => ({
        ...item,
        parentOrderGuid: data?.guid,
        parentCreatedAt: data?.created_at,
        storeOrderGuid: storeOrder?.guid,
        supplyDate: storeOrder?.supply_date,
        storeOrderStep: storeOrder?.step,
        store_detail: storeOrder?.store,
        delivery: storeOrder?.delivery,
        approved_at: storeOrder?.approved_at,
      })),
    );
  }, [data]);

  const firstOrderItem = allOrderItems[0];

  const isDeliveryInPerson = !Boolean(data?.profile_address);
  const storeOrderStep = allOrderItems?.[0]?.storeOrderStep;

  // ---------------- ORDER STEPS LOGICS ------------------
  const isOrderIncomplete =
    storeOrderStep === StoreOrderSteps.STARTED ||
    storeOrderStep === StoreOrderSteps.PENDING_PAYMENT ||
    storeOrderStep === StoreOrderSteps.PREPAID;

  const isOrderProcessing =
    storeOrderStep === StoreOrderSteps.PROCESSING ||
    storeOrderStep === StoreOrderSteps.SHIPPED ||
    storeOrderStep === StoreOrderSteps.STORE_DELIVERED;

  const isOrderPendingApproval =
    storeOrderStep === StoreOrderSteps.PENDING_PAYMENT_REVIEW ||
    storeOrderStep === StoreOrderSteps.PAYMENT_RECEIVED;

  const isOrderCompleted =
    storeOrderStep === StoreOrderSteps.USER_DELIVERED ||
    storeOrderStep === StoreOrderSteps.COMPLETED;

  const hasOrderShipped = useMemo(() => {
    if (!allOrderItems?.length) return false;
    return (
      storeOrderStep === StoreOrderSteps.SHIPPED ||
      storeOrderStep === StoreOrderSteps.STORE_DELIVERED ||
      storeOrderStep === StoreOrderSteps.COMPLETED
    );
  }, [data, allOrderItems]);

  const {mutate} = usePostUserOrderDelivered(() =>
    navigate('/my-orders?tab=delivered'),
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-dvh">
        <SpinnerLoading size={40} />
      </div>
    );
  }

  if (!data?.guid) {
    return (
      <div className="flex flex-col gap-3 justify-center items-center h-dvh">
        <NoItemFoundPage />
        <Button
          variant="primary"
          className="py-3"
          onClick={() => navigate('/')}
        >
          بازگشت به خانه
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col px-2 w-full bg-white h-dvh">
      <CustomHeader
        preComponent={() => (
          <div
            className="flex relative gap-2 items-center"
            onClick={() => navigate(-1)}
          >
            <i className="icon-nameArrow---Right text-[25px] text-gray-700" />
            <span className="font-medium">جزئیات سفارش</span>
          </div>
        )}
      />

      <div className="flex overflow-y-auto flex-col gap-3 p-3 w-full h-full">
        {isOrderProcessing &&
          isDeliveryInPerson &&
          firstOrderItem.delivery?.delivery_code && (
            <div className="flex flex-col gap-3">
              <div className="flex gap-4 items-center px-4 py-2 w-full text-red-500 bg-red-50 rounded-lg text-md">
                <span>کد تحویل سفارش</span>
                <span>{firstOrderItem.delivery?.delivery_code}</span>
              </div>
              <div className="flex gap-2 items-start text-zinc-500">
                <i className="mt-0.5 text-lg icon-namereport" />
                <span className="text-md">
                  برای دریافت سفارش، کد تایید سفارش را در اختیار فروشنده قرار
                  دهید.
                </span>
              </div>
            </div>
          )}
        {allOrderItems.length > 0 && (
          <>
            <div className="flex flex-col pb-2">
              <span className="py-2 text-sm font-medium text-zinc-500">
                موارد سفارش
              </span>
              {allOrderItems.map(item => (
                <OrderedItems
                  key={item?.guid}
                  title={item?.title_snapshot ?? ''}
                  price={item?.price_snapshot ?? 0}
                  discount_price={item?.discount_price ?? 0}
                  image={item?.image ?? item?.sub_brand_image ?? machineIcon}
                  count={item?.quantity}
                  link={item?.link}
                />
              ))}
            </div>

            <OrderInformation
              createdDate={getPersianDateTime(firstOrderItem?.parentCreatedAt)}
              supplyDate={getPersianDateTime(firstOrderItem?.supplyDate)}
              totalOrderPrice={data?.total_price}
              address={data?.profile_address}
            />

            <BuyerInformation
              buyerName={data?.profile_address?.name ?? supplierProfile?.name}
              buyerAddress={data?.profile_address?.delivery_address}
              buyerPostalCode={data?.profile_address?.postal_code}
              buyerNumber={
                supplierProfile?.phone_number ?? supplierProfile?.username
              }
              buyerBlockNumber={data?.profile_address?.building_number}
              buyerCity={data?.profile_address?.city_name}
              buyerState={data?.profile_address?.state_name}
            />

            <SellerInformation
              storeName={firstOrderItem?.store_detail?.title}
              state={firstOrderItem?.store_detail?.state}
              city={firstOrderItem?.store_detail?.city}
              address={firstOrderItem?.store_detail?.address}
              storeNumber={firstOrderItem?.store_detail?.phone_number}
              isOrderCompleted={isOrderCompleted}
            />

            <OrderSupplyingStatus
              storeOrderStep={firstOrderItem?.storeOrderStep}
              hasOrderShipped={hasOrderShipped}
              deliveryDate={getPersianDateTime(
                firstOrderItem?.delivery?.delivery_date,
              )}
              storeDeliveryMethod={firstOrderItem?.delivery?.shipping_method}
              approved_at={getPersianDateTime(firstOrderItem?.approved_at)}
              tracingCode={firstOrderItem?.delivery?.shipment_number}
              receiptImage={firstOrderItem?.delivery?.shipping_receipt}
              isDeliveryInPerson={isDeliveryInPerson}
            />

            {isOrderIncomplete && (
              <span className="text-sm text-gray-600">
                جهت تکمیل سفارش و ادامه خرید، مبلغ سفارش را پرداخت کنید.
              </span>
            )}
            {(storeOrderStep === StoreOrderSteps.SHIPPED ||
              storeOrderStep === StoreOrderSteps.STORE_DELIVERED ||
              (storeOrderStep === StoreOrderSteps.PROCESSING &&
                isDeliveryInPerson)) && (
              <CustomCheckbox
                customClass="gap-1"
                name="hasOrderReceived"
                title="سفارش را تحویل گرفتم"
                onChange={() => setOrderReceived(prev => !prev)}
                checked={orderReceived}
              />
            )}

            <div className="flex sticky -bottom-5 gap-3 justify-center pt-1 pb-4 mt-auto w-full bg-white border-t">
              {isOrderIncomplete && (
                <>
                  <Button
                    size="medium"
                    className="w-full h-12"
                    onClick={() => setIsPayFlowOpen(true)}
                  >
                    پرداخت
                  </Button>
                </>
              )}

              {isOrderCompleted && (
                <Button
                  size="medium"
                  className="w-full h-12"
                  onClick={() => navigate(-1)}
                >
                  بستن
                </Button>
              )}

              {orderReceived && (
                <Button
                  size="medium"
                  className="w-full h-12"
                  onClick={() =>
                    mutate({
                      store_order_guid: storeOrderGuid!,
                      order_guid: orderGuid!,
                    })
                  }
                >
                  ثبت
                </Button>
              )}

              {(isOrderProcessing || isOrderPendingApproval) &&
                !orderReceived && (
                  <>
                    <Button
                      size="medium"
                      variant="outline"
                      className="w-36"
                      onClick={() => navigate('/backup')}
                    >
                      پشتیبانی
                    </Button>
                    <Button
                      size="medium"
                      className="w-36"
                      onClick={() => navigate(-1)}
                    >
                      متوجه شدم
                    </Button>
                  </>
                )}
            </div>
          </>
        )}

        <LendTechPaySheet
          isOpen={isPayFlowOpen}
          setIsOpen={setIsPayFlowOpen}
          transactionType={TransactionTypesChoices.STORE_ORDER}
          orderGuid={orderGuid}
          storeOrderGuid={storeOrderGuid}
        />
      </div>
    </div>
  );
};

export default OrderDetail;
